















import {Component, Vue, Watch} from 'vue-property-decorator';
import {colors, Platform} from 'quasar';
import {
	FeatureFlag,
	SessionStore,
	WebService,
	SpecialsStore,
	TicketsStore,
	AnalyticsService,
	TrackingService,
	PushService,
	UIStore,
	LocationService,
	CustomerProfileStore,
	OrderStore,
	DeepLinkStore,
	EventsService,
	DependencyService,
	ERouteName,
	PERMISSION_STATE,
	BRAND,
	BRAND_CONFIG_COLORS,
	BRAND_CONFIG_IMAGES,
	BRAND_CONFIG_IMAGES_BASE64,
	RoutingUtils,
} from '@customer-portal/shared';
import router from '@/router';
import LoadingOverlay from '@/components/LoadingOverlay.vue';
import {useFavicon} from '@vueuse/core';
import PermissionsWrapper from './components/PermissionsWrapper.vue';
import type {colorsRgba} from 'quasar/dist/types/utils/colors';
import {SplashScreen} from '@cap/@capacitor/splash-screen';
import {App as CapacitorApp} from '@cap/@capacitor/app';
import {NetworkConnectivityBanner} from '@customer-portal/shared/src/components';
import type {
	IAuthConfig,
	IBrandConfigVariables,
	TBrandConfigImageBase64,
} from '@customer-portal/shared';


interface ICSSProperties extends IBrandConfigVariables {
	subtitleColor: string;
	titleColor: string;
}


@Component({
	components: {
		LoadingOverlay,
		NetworkConnectivityBanner,
		PermissionsWrapper
	}
})
export default class App extends Vue {
	showLoadingOverlay = true;
	cssProperties: ICSSProperties = {
		// colors from config service
		actionPrimaryColor: '#CC2940', // action
		backgroundColor: '#F2EEE3',
		backgroundSecondaryColor: '#F44D21', // #6
		bootScreenColor: '#8A2432',
		fullscreenBackgroundColor: '#F44D21', // #7
		giftCardColor: '#211F20',
		memberCardColor: '#8A2432',
		navColor: '#8A2432',
		pointsTextColor: '#6A5357',
		qrPageColor: '#211F20',
		rewardClaimedScreenColor: '#8a2432',
		singleHighlightColor: '#F44D21', // #4

		appFont: 'Arial',
		largeTextFont: 'Arial',

		giftCardBackgroundImage: 'none',
		loginBackgroundImage: 'none',
		loyaltyInfoBackgroundImage: 'none',
		loyaltySignupRewardImage: 'none',
		memberCardBackgroundImage: 'none',
		qrPageBackgroundImage: 'none',

		titleColor: '#000000',
		subtitleColor: 'var(--color-gray)',
	};

	get brandConfigAsCssProperties(): Record<string, string> {
		const variables: Record<string, string> = {};
		const brandConfigKeys = Object.keys(this.cssProperties) as Array<keyof IBrandConfigVariables>;

		// Initialize variables with the default values.
		brandConfigKeys.forEach(brandConfigKey => {
			const appInitialValue = this.cssProperties[brandConfigKey];

			variables[`--${brandConfigKey}`] = appInitialValue;

			// generate additional colors
			if(appInitialValue.startsWith('#')) {
				variables[`--${brandConfigKey}RGB`] = this.rgbaObjectToRGBString(colors.hexToRgb(appInitialValue));
				variables[`--${brandConfigKey}Light`] = colors.lighten(appInitialValue, 50);
				variables[`--${brandConfigKey}LightRGB`] = this.rgbaObjectToRGBString(colors.hexToRgb(variables[`--${brandConfigKey}Light`]));
			}
		});

		return variables;
	}

	get authConfig() {
		return SessionStore.authConfig;
	}

	get brandConfig() {
		return SessionStore.brandConfig;
	}

	get selectedBackground() {
		return SessionStore.selectedBackground;
	}

	get customerProfile() {
		return CustomerProfileStore.customerProfile;
	}

	get brandName() {
		return SessionStore.brandConfig?.brandName || '';
	}

	// Instead of using :style on the q-app element we set these
	// on the document root element so that we can use them in
	// modals as well
	@Watch('brandConfigAsCssProperties', {deep: true})
	onCssPropertiesChange() {
		for(const key in this.brandConfigAsCssProperties) {
			document.documentElement.style.setProperty(key, this.brandConfigAsCssProperties[key]);
		}
	}

	@Watch('customerProfile', {immediate: true})
	async onCustomerProfileChange() {
		if(this.customerProfile && this.authConfig !== null) {
			await WebService.loggedInStarted;
			if(!this.customerProfile.acceptedTOS && this.$route.name !== ERouteName.AcceptTOS) {
				await this.$router.push({
					name: ERouteName.AcceptTOS
				});
			}
		}
	}

	@Watch('authConfig')
	async onAuthChange() {
		if(!WebService.started) {
			try {
				await WebService.start();
			} catch(error) {
				console.error(error);
				// If we couldn't start then it might mean that this is first launch and something
				// is not in prod yet (default env). If there is no stored authConfig then let's redirect
				// to login (which happens below)
			}
		}
		if(this.authConfig === null) {
			EventsService.reset();
			AnalyticsService.logout();
			this.showLoadingOverlay = true;

			if(this.$router.currentRoute.name !== ERouteName.Login) {
				/*
				 * Almost certainly, we're arriving here through the
				 * Launch page and a process that should've initialized
				 * SessionStore.initialPath. If initialPath matches a
				 * route that can be accessed anonymously, redirect to
				 * that route. Otherwise, reroute to Login.
				 */
				const initialRoute = SessionStore.initialRoute;
				if(initialRoute &&
					initialRoute.meta?.anonymous === true
				) {
					const location = RoutingUtils.generateLocationFromRoute(initialRoute);
					await this.$router.push(location);
				} else {
					await this.$router.push({name: ERouteName.Login});
				}
				await SplashScreen.hide();
			}
			this.showLoadingOverlay = false;
		} else {
			EventsService.setUser(this.authConfig.userBase);
			this.showLoadingOverlay = true;
			await WebService.startLoggedIn(this.authConfig);
			// Wait to redirect until after the user has logged in
			if(this.customerProfile) {
				if(Platform.is.capacitor && this.customerProfile && this.customerProfile.pushNotificationsEnabled) {
					PushService.startupRegister();
				}
				AnalyticsService.login();
				WebService.handleScreenPops();
			}

			try {
				await SessionStore.fetchBrandConfig();
				this.showLoadingOverlay = false;
			} catch(e) {
				console.error('Failed to fetch brand config.', e);
				WebService.showError(e);
			}
		}

		// Fix background if it's not selectable
		if(this.brandConfig) {
			// If none is set, or it's out of bounds, then reset to 0
			const backgroundImageMax = this.brandConfig.backgroundImages.length - 1;
			if(!this.selectedBackground || this.selectedBackground > backgroundImageMax) {
				SessionStore.setSelectedBackground(0);
			}
		}

		// Clean up Vuex store
		if(!SpecialsStore.currentSpecialIndex) {
			SpecialsStore.setCurrentSpecialIndex(0);
		}

		// Just in case we hide one more time
		await SplashScreen.hide();
	}

	@Watch('brandConfig', {immediate: true, deep: true})
	async onBrandConfigChange() {
		if(this.brandConfig) {
			BRAND_CONFIG_COLORS.forEach(configName => {
				// Fallback to initial value if it's not set
				// This should give us backwards compatibility while
				// we wait for the server to start providing it
				const color = this.brandConfig?.[configName] ?? 'initial';
				Vue.set(this.cssProperties, configName, color);
			});

			for(const configName of BRAND_CONFIG_IMAGES) {
				// Fallback to initial value if it's not set
				// This should give us backwards compatibility while
				// we wait for the server to start providing it
				if(this.brandConfig?.[configName]) {
					let image = `url(${this.brandConfig[configName]})`;

					// Fix for flickering images
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
					if(BRAND_CONFIG_IMAGES_BASE64.includes(configName as TBrandConfigImageBase64)) {
						image = `url(${await WebService.base64Image(this.brandConfig[configName])})`;
					}

					if(this.cssProperties[configName] !== image) {
						Vue.set(this.cssProperties, configName, image);
					}
				} else {
					Vue.set(this.cssProperties, configName, 'none');
				}
			}

			const favicon = useFavicon();
			if(favicon) {
				favicon.value = this.brandConfig.logoImage;
			}

			// Fonts
			this.cssProperties.appFont = this.brandConfig?.appFont ?? 'Arial';
			this.cssProperties.largeTextFont = this.brandConfig?.largeTextFont ?? 'Arial';

			// Brand config might have returned after the customHTML global route guard has executed, so manually trigger
			// an injection just in case. If the html has already been injected for the route, the service won't inject it again
			// Commenting out until full C deploy
			// CustomHTMLService.handleNavigationInjection(router.currentRoute);
		}
	}

	async sleep(milliseconds: number) {
		return new Promise(resolve => {
			return setTimeout(resolve, milliseconds);
		});
	}

	// We check for the auth GET param as well as for a tempAuthConfig.
	// If we see the auth GET param, we use that and put it into tempAuthConfig so that
	// if the user refreshes the page, we can use the auth from tempAuthConfig.
	// We try up to 10 times for the auth to work and then we give up.
	async checkForAuth() {
		let authChanged = false;
		// We have to do this "native" because Vue doesn't have this all wired up this early
		let auth: IAuthConfig | null = null;
		let search: string = window.location.search;
		// This is for when you are running the dev server locally which uses
		// the hash for the path. We need to check the hash for the auth param
		if(process.env.NODE_ENV === 'development' && location.hash !== '') {
			if(location.hash.indexOf('?') !== -1) {
				search = location.hash.substring(location.hash.indexOf('?'));
			}
		}
		const params = new URLSearchParams(search);
		if(params.has('auth')) {
			const authParam = params.get('auth');
			if(authParam) {
				auth = JSON.parse(atob(authParam)) as IAuthConfig;
				SessionStore.setTempAuthConfig(auth);
			}
		} else if(SessionStore.tempAuthConfig !== null) {
			auth = SessionStore.tempAuthConfig;
		}

		if(auth && 'userBase' in auth && 'apiKey' in auth && 'franchisorBase' in auth) {
			// If we got auth at all assume it changed
			authChanged = true;
			let authSuccessful = false;
			for(let i = 0; i < 10; i++) {
				if(await WebService.testAuthConfig(auth)) {
					authSuccessful = true;
				} else {
					// Wait 1 second between tries
					await this.sleep(1000);
				}
				if(authSuccessful) {
					break;
				}
			}
			// Always clear
			SessionStore.clearTempAuthConfig();
			if(authSuccessful) {
				SessionStore.setAuthConfig(auth);
			} else {
				// Assume it didn't change if we couldn't validate it
				authChanged = false;
			}
		}


		return authChanged;
	}

	/**
	 * Spot to add init calls to stores that need to run some code at startup
	 *
	 * Calls should be non-blocking (no await) unless strictly necessary.
	 */
	async initStores(appInit = false) {
		return Promise.all([
			OrderStore.init(appInit),
			TicketsStore.init(),
		]);
	}

	async created() {
		SessionStore.setBrand(BRAND.GENERIC);
		DependencyService.setRouter(router);
		EventsService.registerAppInfo(await WebService.getDomain(), WebService.getAppVersion());

		await this.initStores(true);

		// Go ahead and attach the global router
		// Brand Config might not be available (no data in SessionStore) and the request hasn't returned
		// If that's the case, when the service the guard uses checks to see what to inject, it will get an
		// empty array and not inject anything
		// Commenting out until full C deploy
		// router.beforeEach(InjectCustomHTML);

		// We need to check this first so we can grab the auth from the URL before we redirect
		const authCheck = this.checkForAuth();
		if(this.$route.name !== 'Launch') {
			const path = WebService.getCurrentPath();
			SessionStore.setInitialPath(path);
			await this.$router.push({
				name: ERouteName.Launch,
				params: {
					bounceToHome: 'false' // String booleans are gross but it's all that's allowed
				}
			});
		}
		const authChanged = await authCheck;
		await AnalyticsService.init();

		CapacitorApp.removeAllListeners();
		if(Platform.is.capacitor) {
			CapacitorApp.addListener('appUrlOpen', (data) => {
				if(this.customerProfile) {
					console.log('appUrlOpen', data);
					if(data.url.indexOf('tp') === 0) {
						// This is a deep link
						DeepLinkStore.handleDeepLink(data.url);
					} else {
						WebService.processOpenUrl(data.url);
					}
				}
			});
			// Here is where we will handle our redirects, you can only have 1
			const launchUrl = await CapacitorApp.getLaunchUrl();
			if(launchUrl && launchUrl.url && launchUrl.url !== '') {
				if(launchUrl.url.indexOf('tp') === 0) {
					// This is a deep link
					DeepLinkStore.handleDeepLink(launchUrl.url);
				} else {
					SessionStore.setInitialPath(launchUrl.url);
				}
			}
		}
		CapacitorApp.addListener('appStateChange', async (state) => {
			// N.B.: This fires on a tab change or when the browser regains focus (on desktop)
			if(state.isActive) {
				console.log('resuming app');
				TrackingService.trackEvent(TrackingService.EVENT_TYPE_APP_OPEN).then();
				if(WebService.started) {
					await WebService.reload();
				}
				// Init stores again, this does stuff like refresh the ticket
				this.initStores().catch((err) => {
					console.error(err);
				});
				// Recheck push ability
				if(Platform.is.capacitor && this.customerProfile && this.customerProfile.pushNotificationsEnabled) {
					await PushService.startupRegister();
				}
				if(Platform.is.capacitor && SessionStore.isEnabled(FeatureFlag.PUSH_NOTIFICATIONS) && this.customerProfile && UIStore.showPushNotificationDisabled) {
					// Check to see if they fixed it in settings and then came back
					if(await PushService.requestPermission()) {
						UIStore.setShowPushNotificationDisabled(false);
					}
				}
				if(Platform.is.capacitor) {
					const locationState = await LocationService.getPermissionState(); // Force vuex update
					if(locationState === PERMISSION_STATE.GRANTED) {
						// If we are granted then turn off both these if they are open
						UIStore.setShowGeolocationRequest(false);
						UIStore.setShowGeolocationDisabled(false);
					} else if(locationState === PERMISSION_STATE.DENIED && UIStore.showGeolocationRequest) {
						// If we are denied and we were showing the request then let's show disabled
						UIStore.setShowGeolocationRequest(false);
						UIStore.setShowGeolocationDisabled(true);
					}
				}
			} else {
				TrackingService.trackEvent(TrackingService.EVENT_TYPE_APP_CLOSE).then();
			}
		});

		// We are doing this here because we first want to check for the auth GET param
		if(!authChanged) {
			await this.onAuthChange();
		}
	}

	destroyed() {
		CapacitorApp.removeAllListeners();
		EventsService.unregisterAppInfo();
	}

	rgbaObjectToRGBString(colorObject: colorsRgba): string {
		return `${colorObject.r},${colorObject.g},${colorObject.b}`;
	}
}
